import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../helpers/axiosInstance';
import { MdOutlineMonitor } from 'react-icons/md';
import { TbWorld } from 'react-icons/tb';
import { TbHash } from 'react-icons/tb';
import { MdOutlineEmail } from 'react-icons/md';

const DeviceApproval = () => {
  const [devices, setDevices] = useState([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const fetchPendingDevices = async () => {
    try {
      const res = await axiosInstance.get(`/devices/pending`);
      setDevices(res.data);
    } catch (err) {}
  };

  const updateStatus = async (id: string, status: 'approved' | 'rejected') => {
    setLoadingId(id + '_' + status);
    try {
      await axiosInstance.patch(`/devices/${id}/status`, { status });
      toast.success(`Device ${status} successfully`);
      fetchPendingDevices();
    } catch (err) {
      toast.error(`Failed to ${status} device`);
    } finally {
      setLoadingId(null);
    }
  };

  useEffect(() => {
    fetchPendingDevices();
  }, []);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Pending Device Approvals
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Review and approve new device access requests
        </p>
      </div>

      {/* Empty state */}
      {devices.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg
              className="w-7 h-7 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-gray-700 font-semibold text-base">
            All caught up!
          </p>
          <p className="text-gray-400 text-sm mt-1">
            No pending device approvals at the moment.
          </p>
        </div>
      )}

      {/* Device Cards */}
      <div className="space-y-3">
        {devices.map((device: any) => (
          <div
            key={device._id}
            className="bg-white border border-gray-200 rounded-xl shadow-sm px-5 py-4 flex flex-row items-center justify-between gap-4 transition-shadow hover:shadow-md"
          >
            {/* Left — User + Device Info */}
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                }}
              >
                {device.userId?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>

              <div className="space-y-0.5">
                {/* Name */}
                <p className="text-gray-900 font-semibold text-sm">
                  {device.userId?.name}
                </p>

                {/* Email */}
                <p className="text-gray-500 text-xs flex items-center gap-1.5">
                  <MdOutlineEmail className="w-3.5 h-3.5 shrink-0" />
                  {device.userId?.email}
                </p>

                {/* OS */}
                <p className="text-gray-500 text-xs flex items-center gap-1.5">
                  <MdOutlineMonitor className="w-3.5 h-3.5 shrink-0" />
                  {device.os || '—'} {device.osVersion || ''}
                </p>

                {/* Browser */}
                <p className="text-gray-500 text-xs flex items-center gap-1.5">
                  <TbWorld className="w-3.5 h-3.5 shrink-0" />
                  {device.browser || '—'}
                </p>

                {/* Device ID */}
                <p className="text-gray-400 text-xs font-mono flex items-center gap-1.5">
                  <TbHash size={14} className="shrink-0" />
                  {device.deviceId}
                </p>
              </div>
            </div>

            {/* Right — Action Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Approve */}
              <button
                onClick={() => updateStatus(device._id, 'approved')}
                disabled={loadingId !== null}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-opacity shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#16a34a' }}
              >
                {loadingId === device._id + '_approved' ? (
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                Approve
              </button>

              {/* Reject */}
              <button
                onClick={() => updateStatus(device._id, 'rejected')}
                disabled={loadingId !== null}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-opacity shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#dc2626' }}
              >
                {loadingId === device._id + '_rejected' ? (
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeviceApproval;
